'use client';

import { Box, Flex, IconButton } from '@coolblue-development/freddy';
import clickOnInformationIconTrigger from '@coolblue-development/ga4-events/triggers/ComparisonTool/clickOnInformationIconTrigger';
import { InfoCircled } from '@coolblue-development/icons';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { PropertyExplanationModal } from '@coolblue-development/next-hemingway/client';
import { useTranslation } from '@i18n/useTranslation';
import { type MouseEvent, useState } from 'react';

import { propertyByProductIdAndPropertyId } from '../../service/ProductPropertyService';
import type { Product, Property } from '../../typings/types';
import { PropertyColumn } from './PropertyColumn';
import {
  mobileStickyHeaderStyles,
  propertyIconButtonContainerStyles,
  propertyRowStyles
} from './PropertyColumn.styles';

type PropertyRowProps = {
  property: Property;
  products: Product[];
  productTypeId: number | null;
};

const PropertyRow = ({ property, products, productTypeId }: PropertyRowProps) => {
  const { trackEvent } = useAnalytics();
  const { t } = useTranslation('webshop');

  const [showPropertyExplanation, setShowPropertyExplanation] = useState<boolean>(() => false);

  function togglePropertyExplanationModal(open: boolean, event?: MouseEvent) {
    if (event) {
      event.preventDefault();
    }

    if (!property.hasExplanation || open === showPropertyExplanation) return;

    setShowPropertyExplanation(open);

    if (showPropertyExplanation) {
      trackEvent(
        clickOnInformationIconTrigger({
          subject_id: `${property.displayName}, ${property.propertyId}, ${productTypeId}`
        })
      );
    }
  }

  const values = products.map(
    product => propertyByProductIdAndPropertyId(products, product.id, property.propertyId)?.value
  );

  const allValuesAreSame = values.every((val, _i, arr) => val === arr[0]);

  function buildPropertyRowClassname(allValuesAreSame: boolean, hasExplanation: boolean) {
    let className = '';

    if (allValuesAreSame) {
      className = 'all-values-are-same';
    }

    if (hasExplanation) {
      className += ' has-explanation';
    }

    return className.trim();
  }

  return (
    <Box
      className={`property-row ${buildPropertyRowClassname(allValuesAreSame, property.hasExplanation)}`}
      onClick={event => togglePropertyExplanationModal(true, event)}
      sx={propertyRowStyles}
    >
      <Box hideFrom='lg' sx={mobileStickyHeaderStyles}>
        <IconButton
          hideUntil='md'
          aria-label={t('compare.propertyExplanationTrigger.label', {
            propertyName: property.displayName
          })}
          component='a'
          href={`https://www.coolblue.nl/property-explanations/${productTypeId}/${property.propertyId}`}
          sx={{
            paddingLeft: 0
          }}
        >
          <InfoCircled />
        </IconButton>
        <strong>{property.displayName}</strong>
      </Box>

      <Flex
        component='ul'
        inline
        m={0}
        p={0}
        sx={{
          listStyleType: 'none',
          width: '100%'
        }}
      >
        <PropertyColumn>
          <Box sx={propertyIconButtonContainerStyles}>
            {property.hasExplanation && productTypeId && (
              <>
                {showPropertyExplanation && (
                  <PropertyExplanationModal
                    open={showPropertyExplanation}
                    onClose={() => togglePropertyExplanationModal(false)}
                    productTypeId={String(productTypeId)}
                    property={property}
                  />
                )}

                <IconButton
                  aria-label={t('compare.propertyExplanationTrigger.label', {
                    propertyName: property.displayName
                  })}
                  component='a'
                  href={`https://www.coolblue.nl/property-explanations/${productTypeId}/${property.propertyId}`}
                >
                  <InfoCircled />
                </IconButton>
              </>
            )}
          </Box>
          <strong>{property.displayName}</strong>
        </PropertyColumn>

        {values.map((value, index) => (
          <PropertyColumn key={`${property.displayName}_${value}_${index}`}>{value}</PropertyColumn>
        ))}
      </Flex>
    </Box>
  );
};

export { PropertyRow };
